import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { toast } from "sonner";
import { createAndAddOrganisationMemberMutation } from "@/app/Organisations/GraphQL/organisationMemberMutations";

type FormValues = {
  name: string;
  email: string;
};

const validationSchema = z.object({
  name: z.string().min(1, { error: "Name is required" }),
  email: z.email({ error: "A valid email is required" }),
});

export function AddOrganisationMemberDialog({
  organisationId,
}: {
  organisationId: string;
}) {
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(
    createAndAddOrganisationMemberMutation,
  );

  return (
    <BaseDialog
      title="Add member"
      subtitle="Add a user to this organisation. If the user doesn't exist, they'll be created and emailed a temporary password."
      size="md"
    >
      <GenericForm<FormValues>
        initialValues={{
          name: "",
          email: "",
        }}
        validationSchema={toFormikValidationSchema(validationSchema)}
        onSubmit={async (values) => {
          await mutateAsync({
            organisation_id: organisationId,
            name: values.name,
            email: values.email,
          });

          toast.success("Member added to organisation");
          close();
        }}
      >
        <TextInput
          name="name"
          label="Full Name"
          className="col-span-full"
          autoFocus
        />
        <TextInput
          name="email"
          label="Email"
          type="email"
          className="col-span-full"
        />
        <DialogSubmitButton text="Add member" />
      </GenericForm>
    </BaseDialog>
  );
}
